import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import type { GraphemeMorphemeMnemonicsPanelMorphemeUserSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";

// Neutral default for consumers that read this slice via a non-suspense `useQuery` (see
// `review-card-morpheme-layout-suspense.tsx`): covers the first paint before the initial read
// resolves. "Not favorited, not studied" — the same state a brand-new morpheme has.
export const EMPTY_MORPHEME_MNEMONICS_PANEL_MORPHEME_USER_SLICE = {
  enrolledPinyinList: [],
  favoritesCollectionId: null,
  isFavorited: false,
  meaningCard: null,
} as const satisfies GraphemeMorphemeMnemonicsPanelMorphemeUserSlice;

const MORPHEME_USER_SLICE_TABLES = [
  "user_grapheme_meaning_card",
  "user_grapheme_pronunciation_card",
  "user_card",
  "fsrs_card",
  "collection",
  "collection_morpheme",
] as const;

export function useGetMorphemeMnemonicsPanelMorphemeUserSliceQueryKey() {
  const withTableChangeVersion = useTableChangeVersion(
    tableNameMatcher(MORPHEME_USER_SLICE_TABLES),
  );
  const getMeaningCardQueryKey = actorQueries(
    import.meta.location,
  ).userGraphemeMeaningCard.findFirst.useGetQueryKey({
    with: { userCard: { with: { fsrsCard: true } } },
  });
  const getPronunciationCardListQueryKey = actorQueries(
    import.meta.location,
  ).userGraphemePronunciationCard.findMany.useGetQueryKey({});
  const getFavoritesCollectionQueryKey = actorQueries(
    import.meta.location,
  ).collection.findFirst.useGetQueryKey({});
  const getCollectionMorphemeListQueryKey = actorQueries(
    import.meta.location,
  ).collectionMorpheme.findMany.useGetQueryKey({});

  return (grapheme: string) => {
    const meaningCardQueryOptions = actorQueries(
      import.meta.location,
    ).userGraphemeMeaningCard.findFirst.getQueryOptions(getMeaningCardQueryKey, {
      where: { grapheme },
    });
    const pronunciationCardListQueryOptions = actorQueries(
      import.meta.location,
    ).userGraphemePronunciationCard.findMany.getQueryOptions(getPronunciationCardListQueryKey, {
      where: { grapheme },
    });
    const favoritesCollectionQueryOptions = actorQueries(
      import.meta.location,
    ).collection.findFirst.getQueryOptions(getFavoritesCollectionQueryKey, {
      where: { isFavorites: true },
    });
    const collectionMorphemeListQueryOptions = actorQueries(
      import.meta.location,
    ).collectionMorpheme.findMany.getQueryOptions(getCollectionMorphemeListQueryKey, {
      where: { morpheme: grapheme },
    });

    return withTableChangeVersion([
      "mnemonicsPanelMorphemeUserSlice",
      grapheme,
      {
        collectionMorphemeListQueryOptions,
        favoritesCollectionQueryOptions,
        meaningCardQueryOptions,
        pronunciationCardListQueryOptions,
      },
    ] as const);
  };
}

export const [
  useMorphemeMnemonicsPanelMorphemeUserSliceQueryOptions,
  morphemeMnemonicsPanelMorphemeUserSliceQueryFn,
] = createUseQueryOptions(
  useGetMorphemeMnemonicsPanelMorphemeUserSliceQueryKey,
  async ({
    client,
    queryKey: [
      _,
      _grapheme,
      {
        collectionMorphemeListQueryOptions,
        favoritesCollectionQueryOptions,
        meaningCardQueryOptions,
        pronunciationCardListQueryOptions,
      },
    ],
  }) => {
    const [meaningCardRow, pronunciationCardList, favoritesCollection, collectionMorphemeList] =
      await Promise.all([
        client.fetchQuery(meaningCardQueryOptions),
        client.fetchQuery(pronunciationCardListQueryOptions),
        client.fetchQuery(favoritesCollectionQueryOptions),
        client.fetchQuery(collectionMorphemeListQueryOptions),
      ]);
    const favoritesCollectionId = favoritesCollection?.id ?? null;
    const isFavorited =
      favoritesCollectionId !== null &&
      collectionMorphemeList.some(
        (collectionMorpheme) => collectionMorpheme.collectionId === favoritesCollectionId,
      );

    return {
      enrolledPinyinList: pronunciationCardList.map(({ pinyin }) => pinyin),
      favoritesCollectionId,
      isFavorited,
      meaningCard:
        meaningCardRow === null
          ? null
          : {
              description: meaningCardRow.description,
              fsrs: meaningCardRow.userCard?.fsrsCard ?? null,
              id: meaningCardRow.id,
              manuallyMasteredAt: meaningCardRow.userCard?.manuallyMasteredAt ?? null,
              startedStudyingAt: meaningCardRow.userCard?.startedStudyingAt ?? null,
              userCardId: meaningCardRow.userCardId,
            },
    };
  },
);
